import React, { useEffect, useState } from 'react';
import { Form, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { lookupsJson } from '@ourbranch/lookups';

import { useStore } from 'core/store';
import Field from 'core/components/form/form.v2';
import withDatePicker from 'core/components/with-date-picker';
import CountiesAutocomplete from 'common/components/counties-autocomplete';
import AddressAutocomplete from 'core/components/address-autocomplete/address-autocomplete';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';
import { Collapsible } from '../collapsible';
import { MoreInfoNeededFlagItem } from '../add-info-flag-item';
import { HintFlagItem } from '../hint-flag-item';

const PrimaryLocationDetails = ({ sectionsToShow }) => {
  const {
    quote: { getAlerts }
  } = useStore();

  const [shouldShow, setShouldShow] = useState(false);
  const [previousToggleState, setPreviousToggleState] = useState(null);

  const { values, errors, setFieldValue } = useFormikContext();
  const anyAlerts = getAlerts().length > 0;

  const isFarmOffer = isFarmBasedPolicyType(values.policyType) && !!values.address && !!values.state;

  const farmLocation = values.farmLocations?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmLocationAddressPath = `${farmLocationPath}.address`;
  const farmLocationLegalDescPath = `${farmLocationPath}.legalDescription`;

  const { coordinateOptions } = lookupsJson;
  const { legalDescriptionFormatOptions } = lookupsJson.farm;

  const formikErrors = errors && (errors.locationName || errors.numberOfAcres || errors.legalDescriptionFormat);
  const forceOpen = formikErrors;

  // Forreston Rental (Y) mailing address and location address are typically different, so the
  // mask sets disableAddressToggle: true and the use-applicant-address checkbox is suppressed. (#31367)
  // Computed before the sync useEffect so the effect can guard against stale `useFarmLocationApplicantAddress=true`
  // state (provider defaults the flag to true) overwriting the location address on a Y offer.
  const farmPolicyMask = getFarmPolicyMask(values.policyType, values.state);
  const showUseApplicantAddressToggle = !farmPolicyMask?.disableAddressToggle;

  useEffect(() => {
    if (!isFarmOffer) {
      setShouldShow(false);
      return;
    }

    // Show when there are form errors
    if (formikErrors || isFarmOffer) {
      setShouldShow(true);
      return;
    }

    // Default to false for farm offers
    setShouldShow(false);
  }, [sectionsToShow, errors, formikErrors, isFarmOffer, values.farmRisk?.useApplicantAddress]);

  useEffect(() => {
    // When the toggle is masked out (#31367), the location address must be entered separately
    // from the mailing address regardless of `useFarmLocationApplicantAddress`. Skip the sync
    // so stale Formik state doesn't overwrite the rental property address.
    if (!showUseApplicantAddressToggle) {
      return;
    }

    // Sync farm location address with applicant address only when toggle is ON
    const useFarmLocationApplicantAddress = farmLocation?.useFarmLocationApplicantAddress;
    if (useFarmLocationApplicantAddress && values.address) {
      setFieldValue(`${farmLocationAddressPath}.address`, values.address);
      setFieldValue(`${farmLocationAddressPath}.address2`, values.address2);
      setFieldValue(`${farmLocationAddressPath}.city`, values.city);
      setFieldValue(`${farmLocationAddressPath}.state`, values.state);
      setFieldValue(`${farmLocationAddressPath}.zip`, values.zip);
      setFieldValue(`${farmLocationAddressPath}.county`, values.county);
    } else if (previousToggleState && !useFarmLocationApplicantAddress) {
      // Only clear when toggle CHANGED from ON to OFF (not when loading with OFF)
      setFieldValue(`${farmLocationAddressPath}.address`, undefined);
      setFieldValue(`${farmLocationAddressPath}.address2`, undefined);
      setFieldValue(`${farmLocationAddressPath}.city`, undefined);
      setFieldValue(`${farmLocationAddressPath}.state`, undefined);
      setFieldValue(`${farmLocationAddressPath}.zip`, undefined);
      setFieldValue(`${farmLocationAddressPath}.county`, undefined);
    }

    // Control Legal Description Format field based on toggle state
    if (useFarmLocationApplicantAddress) {
      // When toggle is ON: Set to street format
      setFieldValue(`${farmLocationLegalDescPath}.format`, 'street');
    } else if (previousToggleState && !useFarmLocationApplicantAddress) {
      // When toggle changes from ON to OFF: Clear the format value
      setFieldValue(`${farmLocationLegalDescPath}.format`, undefined);
    }

    setPreviousToggleState(useFarmLocationApplicantAddress);
  }, [
    showUseApplicantAddressToggle,
    farmLocation?.useFarmLocationApplicantAddress,
    values.address,
    values.address2,
    values.city,
    values.state,
    values.zip,
    values.county,
    setFieldValue,
    farmLocationAddressPath,
    farmLocationLegalDescPath
  ]);

  // NOTE: Address synchronization for individual risks is now handled by each risk component
  // based on their respective useApplicantAddress checkbox settings (per PR #28262)

  const legalDescriptionFormat = farmLocation?.legalDescription?.format;
  const showFarmHint = isFarmOffer && !anyAlerts;
  // When the toggle is masked out, the location address fields render unconditionally for the
  // chosen legal-description format — `useFarmLocationApplicantAddress` no longer hides them. (#31367)
  const hideLocationAddressFields = showUseApplicantAddressToggle && farmLocation?.useFarmLocationApplicantAddress;

  return (
    <Form>
      {showFarmHint && <HintFlagItem message="Add details about your primary location" />}
      {!showFarmHint && formikErrors && <MoreInfoNeededFlagItem />}
      {shouldShow && (
        <Collapsible title="Primary Location Details" forceOpen={forceOpen} initialOpen={isFarmOffer}>
          {values.address && showUseApplicantAddressToggle && (
            <div style={{ textAlign: 'left', paddingLeft: '12px' }}>
              <Field
                xs={12}
                type="checkbox"
                id={`${farmLocationPath}.useFarmLocationApplicantAddress`}
                name={`${farmLocationPath}.useFarmLocationApplicantAddress`}
                mode="light"
                label={
                  <span>
                    Use <b>{[values.address, values.city, values.state, values.zip].filter(Boolean).join(', ')}</b> as
                    the address for this location
                  </span>
                }
              />
            </div>
          )}
          <Field
            xs={4}
            type="select"
            id={`${farmLocationLegalDescPath}.format`}
            name={`${farmLocationLegalDescPath}.format`}
            mode="light"
            label="Legal Description Format"
            options={legalDescriptionFormatOptions}
            disabled={hideLocationAddressFields}
            onChange={(value) => {
              // Default legalDescriptionFormat values
              if (value === 'section-township-range') {
                setFieldValue(`${farmLocationLegalDescPath}.townshipCoordinates`, 'unknown');
                setFieldValue(`${farmLocationLegalDescPath}.rangeCoordinates`, 'unknown');
              }
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${farmLocationPath}.numberOfAcres`}
            name={`${farmLocationPath}.numberOfAcres`}
            mode="light"
            label="Acres"
          />
          <Field
            xs={4}
            type="string"
            id={`${farmLocationPath}.description`}
            name={`${farmLocationPath}.description`}
            mode="light"
            label="Location Name"
          />
          {legalDescriptionFormat === 'street' && !hideLocationAddressFields && (
            <>
              <AddressAutocomplete
                id={`${farmLocationAddressPath}.address`}
                name={`${farmLocationAddressPath}.address`}
                xs={12}
                mode="light"
                label="Address*"
                riskAddress
              />
              <Field
                xs={12}
                type="string"
                id={`${farmLocationAddressPath}.address2`}
                name={`${farmLocationAddressPath}.address2`}
                mode="light"
                label="Apt, unit, building, etc."
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.city`}
                name={`${farmLocationAddressPath}.city`}
                mode="light"
                label="City*"
              />
              <Field
                xs={4}
                type="state"
                id={`${farmLocationAddressPath}.state`}
                name={`${farmLocationAddressPath}.state`}
                mode="light"
                label="State*"
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.zip`}
                name={`${farmLocationAddressPath}.zip`}
                mode="light"
                label="Zip Code*"
              />
            </>
          )}
          {legalDescriptionFormat === 'section-township-range' && (
            <>
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.city`}
                name={`${farmLocationAddressPath}.city`}
                mode="light"
                label="City*"
              />
              <Field
                xs={4}
                type="state"
                id={`${farmLocationAddressPath}.state`}
                name={`${farmLocationAddressPath}.state`}
                mode="light"
                label="State*"
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.zip`}
                name={`${farmLocationAddressPath}.zip`}
                mode="light"
                label="Zip code*"
              />
              <CountiesAutocomplete state={'IL'} setValues={setFieldValue} id={`${farmLocationAddressPath}.county`} />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationLegalDescPath}.section`}
                name={`${farmLocationLegalDescPath}.section`}
                mode="light"
                label="Section"
              />
              <Field
                xs={2}
                type="string"
                id={`${farmLocationLegalDescPath}.township`}
                name={`${farmLocationLegalDescPath}.township`}
                mode="light"
                label="Township"
              />
              <Field
                xs={2}
                type="select"
                id={`${farmLocationLegalDescPath}.townshipCoordinates`}
                name={`${farmLocationLegalDescPath}.townshipCoordinates`}
                mode="light"
                label="Coordinates"
                options={coordinateOptions}
              />
              <Field
                xs={2}
                type="string"
                id={`${farmLocationLegalDescPath}.range`}
                name={`${farmLocationLegalDescPath}.range`}
                mode="light"
                label="Range"
              />
              <Field
                xs={2}
                type="select"
                id={`${farmLocationLegalDescPath}.rangeCoordinates`}
                name={`${farmLocationLegalDescPath}.rangeCoordinates`}
                mode="light"
                label="Coordinates"
                options={coordinateOptions}
              />
            </>
          )}

          {legalDescriptionFormat === 'lot-block' && (
            <>
              <Field
                xs={6}
                type="numeric"
                id={`${farmLocationLegalDescPath}.lot`}
                name={`${farmLocationLegalDescPath}.lot`}
                mode="light"
                label="Lot"
              />
              <Field
                xs={6}
                type="numeric"
                id={`${farmLocationLegalDescPath}.block`}
                name={`${farmLocationLegalDescPath}.block`}
                mode="light"
                label="Block"
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.city`}
                name={`${farmLocationAddressPath}.city`}
                mode="light"
                label="City*"
              />
              <Field
                xs={4}
                type="state"
                id={`${farmLocationAddressPath}.state`}
                name={`${farmLocationAddressPath}.state`}
                mode="light"
                label="State*"
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.zip`}
                name={`${farmLocationAddressPath}.zip`}
                mode="light"
                label="Zip code*"
              />
              <CountiesAutocomplete
                id={`${farmLocationAddressPath}.county`}
                state={'IL'}
                setValues={setFieldValue}
                xs={12}
              />
            </>
          )}
          {legalDescriptionFormat === 'long-legal' && (
            <>
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.city`}
                name={`${farmLocationAddressPath}.city`}
                mode="light"
                label="City*"
              />
              <Field
                xs={4}
                type="state"
                id={`${farmLocationAddressPath}.state`}
                name={`${farmLocationAddressPath}.state`}
                mode="light"
                label="State*"
              />
              <Field
                xs={4}
                type="string"
                id={`${farmLocationAddressPath}.zip`}
                name={`${farmLocationAddressPath}.zip`}
                mode="light"
                label="Zip code*"
              />
              <CountiesAutocomplete id={`${farmLocationAddressPath}.county`} state={'IL'} setValues={setFieldValue} />
            </>
          )}
          <Field
            xs={12}
            type="textarea"
            id={`${farmLocationPath}.additionalLegalDescription`}
            name={`${farmLocationPath}.additionalLegalDescription`}
            mode="light"
            label="Additional legal description (optional)"
            minRows={3}
          />
          <Field
            xs={12}
            type="textarea"
            id={`${farmLocationPath}.locationDirections`}
            name={`${farmLocationPath}.locationDirections`}
            mode="light"
            label="Location directions (optional)"
            minRows={3}
          />
        </Collapsible>
      )}
    </Form>
  );
};

PrimaryLocationDetails.propTypes = {
  sectionsToShow: PropTypes.array.isRequired
};

export default withDatePicker(PrimaryLocationDetails);
